import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { useOnboarding } from '../hooks/useOnboarding';

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { operational_ready, loading: onboardingLoading } = useOnboarding();
  const redirectingRef = useRef(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;

    function handleSessionExpired() {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (!redirectingRef.current) {
          redirectingRef.current = true;
          window.location.href = '/login';
        }
      }, 300);
    }

    window.addEventListener('vaerion:session-expired', handleSessionExpired);
    return () => {
      window.removeEventListener('vaerion:session-expired', handleSessionExpired);
      if (timer) clearTimeout(timer);
    };
  }, []);

  if (loading || onboardingLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#07070B]">
        <div className="h-8 w-8 rounded-full bg-[#9B9BA8] animate-pulse" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Redirect to setup wizard if onboarding is not complete
  // Skip redirect if already on /setup or /admin paths
  if (!operational_ready && !location.pathname.startsWith('/setup') && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/setup" replace />;
  }

  return <>{children}</>;
}
